import { CheckIcon, TriangleDownIcon } from "@primer/octicons-react";
import { Select as SelectPrimitive } from "radix-ui";
import type * as React from "react";
import { cn } from "@/lib/utils";

type SelectOption = { value: string; label: React.ReactNode; count?: number };

/** Radix rejects "" as an item value; options may still use it for "none/default". */
const EMPTY = "\u0000empty";
const toItem = (v: string) => (v === "" ? EMPTY : v);
const fromItem = (v: string) => (v === EMPTY ? "" : v);

/**
 * Primer's ActionMenu as a single-select. `prefix` renders a muted "State:"-style
 * label in the trigger for filter menus; `size="sm"` matches small buttons.
 */
function Select({
  value,
  onValueChange,
  options,
  prefix,
  size = "default",
  align = "start",
  className,
  disabled,
  "aria-label": ariaLabel,
  "aria-invalid": ariaInvalid,
}: {
  value: string;
  onValueChange: (value: string) => void;
  options: SelectOption[];
  prefix?: React.ReactNode;
  size?: "default" | "sm";
  align?: "start" | "end";
  className?: string;
  disabled?: boolean;
  "aria-label"?: string;
  "aria-invalid"?: boolean;
}) {
  return (
    <SelectPrimitive.Root value={toItem(value)} onValueChange={(v) => onValueChange(fromItem(v))} disabled={disabled}>
      <SelectPrimitive.Trigger
        data-slot="select-trigger"
        aria-label={ariaLabel}
        aria-invalid={ariaInvalid}
        className={cn(
          "inline-flex w-full min-w-0 items-center gap-1.5 whitespace-nowrap rounded-md border border-border bg-secondary text-left text-secondary-foreground outline-none transition-colors hover:bg-secondary-hover",
          "focus-visible:border-ring focus-visible:shadow-[inset_0_0_0_1px_var(--color-ring)] disabled:cursor-not-allowed disabled:text-faint disabled:hover:bg-secondary",
          "aria-invalid:border-danger-emphasis data-[state=open]:bg-secondary-hover",
          size === "sm" ? "h-7 px-2 text-xs" : "h-8 px-3 text-sm",
          className,
        )}
      >
        {prefix && <span className="text-muted-foreground">{prefix}</span>}
        <span className="min-w-0 flex-1 truncate font-medium">
          <SelectPrimitive.Value />
        </span>
        <SelectPrimitive.Icon asChild>
          <TriangleDownIcon size={16} className="-mr-1 shrink-0 text-muted-foreground" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          data-slot="select-content"
          position="popper"
          align={align}
          sideOffset={4}
          className={cn(
            "z-50 max-h-(--radix-select-content-available-height) min-w-(--radix-select-trigger-width) overflow-hidden rounded-xl border bg-popover text-popover-foreground shadow-overlay",
            "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
          )}
        >
          <SelectPrimitive.Viewport className="p-2">
            {options.map((o) => (
              <SelectPrimitive.Item
                key={o.value}
                value={toItem(o.value)}
                className="relative flex h-8 cursor-pointer select-none items-center gap-2 rounded-md pr-2 pl-8 text-sm text-foreground outline-none data-disabled:pointer-events-none data-disabled:text-faint data-highlighted:bg-accent"
              >
                <SelectPrimitive.ItemIndicator className="absolute left-2 inline-flex">
                  <CheckIcon size={16} />
                </SelectPrimitive.ItemIndicator>
                <SelectPrimitive.ItemText>{o.label}</SelectPrimitive.ItemText>
                {o.count != null && <span className="ml-auto pl-4 text-xs text-muted-foreground tabular-nums">{o.count}</span>}
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}

export { Select, type SelectOption };
